import {
    GetCompanyList,
    GetReportSettingsList,
    useNotification,
    useQueryString,
} from 'hooks'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    CardHeader,
    Header,
    Menu,
    MenuItem,
    Segment,
    Select,
} from 'semantic-ui-react'
import {
    DefaultText,
    PdfImageCompressionTabId,
    ReportControlType,
    ReportSettingsService,
    SettingsText,
    formatSubsectionName,
} from 'utility'
import { useAuth } from 'context'
import { ReportAboutus, UploadBox } from './Forms'
import { PdfImageCompressionSettings } from './PdfImageCompressionSettings'
import styled from 'styled-components'
import { useTabletAndBelowMediaQuery } from 'layout'
import { FlexColumn } from 'components'

const ReportSettingContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    .report-menu {
        width: 100% !important;
        overflow-x: auto;
        ::-webkit-scrollbar {
            height: 5px;
        }
        .active {
            background: ${({ theme }) => theme.colors.body} !important;
        }
    }
    .right-card {
        width: unset !important;
    }
    .ql-container {
        height: 300px;
    }
    .header-footer-box {
        .field {
            padding: 10px;
        }
    }
`

const PDF_COMPRESSION_MENU_ITEM = {
    SubsectionID: PdfImageCompressionTabId,
    SubsectionName: 'PDF image compression',
}

const MenuList = ({ list, activeTab, onTabChange, isMobile }) => (
    <Menu vertical={!isMobile} className="report-menu">
        {list?.map(ele => (
            <MenuItem
                key={ele?.SubsectionID}
                active={
                    String(activeTab?.SubsectionID) ===
                    String(ele?.SubsectionID)
                }
                onClick={() => onTabChange(ele)}
            >
                {formatSubsectionName(ele?.SubsectionName)}
            </MenuItem>
        ))}
    </Menu>
)

const ReportMenuSettings = ({ tab, onSubmit, reportSettings, loading }) => {
    const selecteddata = useMemo(
        () =>
            reportSettings?.find(
                ele => ele?.SubSectionId === tab?.SubsectionID,
            ),
        [reportSettings, tab],
    )

    const submitSettings = async data => {
        const submitData = {
            ReportSettingId: selecteddata?.ReportSettingId || 0,
            SubSectionId: tab?.SubsectionID,
            Answer: data.Answer,
            BackGroundImage: data.BackGroundImage,
            IncludeInTableContent: data.IncludeInTableContent,
            ShowFooter: data.ShowFooter,
            ShowHeader: data.ShowHeader,
        }
        onSubmit(submitData)
    }

    return (
        <>
            {ReportControlType.Upload === tab?.ComponentType && (
                <UploadBox
                    title={tab.tile}
                    data={selecteddata}
                    onSubmit={submitSettings}
                    loading={loading}
                />
            )}
            {ReportControlType.TextArea === tab?.ComponentType && (
                <ReportAboutus
                    loading={loading}
                    data={selecteddata}
                    onSubmit={submitSettings}
                />
            )}
        </>
    )
}
export const ReportSettingsScreen = ({ reportSettingsMenuList }) => {
    const { user } = useAuth()
    const [activeTab, setActiveTab] = useState(null)
    const [processing, setprocessing] = useState('')
    const { data: reportSettings, refetch } = GetReportSettingsList()
    const { options: companyList } = GetCompanyList()
    const navigate = useNavigate()
    const query = useQueryString()
    const reportTab = query.get('reportTab')
    const isMobile = useTabletAndBelowMediaQuery()
    const { successToast, errorToast } = useNotification()

    const canEditPdfCompression =
        user?.Capabilities?.CanEditPdfCompression !== false

    const menuList = useMemo(() => {
        const list = [...(reportSettingsMenuList || [])]
        if (canEditPdfCompression) {
            list.push(PDF_COMPRESSION_MENU_ITEM)
        }
        return list
    }, [reportSettingsMenuList, canEditPdfCompression])

    const isPdfCompressionTab =
        String(activeTab?.SubsectionID) === PdfImageCompressionTabId

    const onTabChange = data => {
        if (data) {
            navigate(`?reportTab=${data.SubsectionID}`)
        }
    }

    useEffect(() => {
        if (!menuList.length) return
        const tab = menuList.find(
            ele => String(ele.SubsectionID) === String(reportTab),
        )
        if (tab) {
            setActiveTab(tab)
        }
    }, [reportTab, menuList])

    useEffect(() => {
        if (!reportTab && menuList.length) {
            onTabChange(menuList[0])
        }
    }, [menuList])

    const onSubmitComponent = async settingData => {
        setprocessing(true)
        try {
            if (settingData?.ReportSettingId) {
                await ReportSettingsService.update({
                    ...settingData,
                })
                successToast(SettingsText.reportSettingsUpdated)
            } else {
                await ReportSettingsService.add(settingData)
                successToast(SettingsText.reportSettingsUpdated)
            }
        } catch {
            errorToast(DefaultText.defaultError)
        } finally {
            setprocessing(false)
            refetch()
        }
    }

    const header = useMemo(
        () =>
            menuList.find(
                ele =>
                    String(ele.SubsectionID) ===
                    String(activeTab?.SubsectionID),
            ),
        [activeTab, menuList],
    )

    return (
        <div>
            <FlexColumn>
                {false && <Select className="mb-3" options={companyList} />}
                <ReportSettingContainer
                    className={`col-12 ${
                        isMobile ? 'flex-column' : 'flex-row'
                    }`}
                >
                    <div className={`${isMobile ? 'col-12' : 'col-3'}`}>
                        <MenuList
                            isMobile={isMobile}
                            list={menuList}
                            activeTab={activeTab}
                            onTabChange={onTabChange}
                        />
                    </div>
                    <Segment raised className="col mt-0 ">
                        {isPdfCompressionTab ? (
                            <PdfImageCompressionSettings />
                        ) : (
                            <>
                                <Header className="mt-1">
                                    <CardHeader>
                                        {formatSubsectionName(
                                            header?.SubsectionName,
                                        )}
                                    </CardHeader>
                                </Header>
                                <ReportMenuSettings
                                    loading={processing}
                                    onSubmit={onSubmitComponent}
                                    tab={activeTab}
                                    reportSettings={reportSettings}
                                />
                            </>
                        )}
                    </Segment>
                </ReportSettingContainer>
            </FlexColumn>
        </div>
    )
}
